import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Table,
  Tooltip,
} from "antd";
import moment from "moment";
import { RootStateOrAny } from "react-redux";
import { isEmpty, uniqueId } from "lodash";
import TextArea from "antd/lib/input/TextArea";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import ModalWindow from "../ModalWindow/ModalWindow";
import { useAppDispatch, useAppSelector } from "../..";
import {
  checkDeactivationBlockingRequest,
  createBlockingRequest,
  deactivateBlockingRequest,
  editBlockingRequest,
  fetchBlockingCurrentState,
  fetchBlockingCurrentStateVersions,
  fetchBlockingRequest,
  fetchBlockingTypes,
  resetBlockingModalData,
} from "../../store/slices/blocking";
import { BlockingObjTypes } from "../../lib/const/blockingObjTypes";
import { BlockingRequestStatusIds } from "../../lib/const/blockingRequestStatusIds";
import { expandedTableEyeIcon, searchIconForInput } from "../../lib/icons";
import {
  displayFormat,
  infinityDateHttpFormat,
  toDisplayFormat,
  toDisplayFormatOnlyTime,
  toHttpFormat,
} from "../../lib/utils/formatDate";
import {
  checkIsLoaded,
  toHttpFormatEndOfDay,
  toHttpFormatStartOfDay,
  checkIsLoading,
  displayFormatWithTime,
} from "../../lib/utils";
import {
  fetchBlockingObjectType,
  fetchBlockingReason,
  fetchBlockingSource,
  fetchRequestStatus,
  fetchUnBlockingReason,
} from "../../store/slices/rtkDictionaries";
import versionsTableUtils from "../../lib/utils/versionsTableUtils";
import pencilWithCalendarIcon from "../../img/pencil-calendar.svg";
import addProperty from "../../img/addProperty.svg";
import DeleteIcon from "../../img/delete.svg";
import { ReactComponent as DotsIcon } from "../../img/3dots.svg";
import tickIcon from "../../img/done.svg";
import deleteIcon from "../../img/delete-round.svg";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { hasAccess } from "../RolesModule/hasAccess";
import { BLOCKING_DEBTS_DEACTIVATION } from "../../lib/const/permissions";
import FileList from "./FileList";
import { deleteContentFileData } from "../../lib/routes/dictionaries";
import HistoryAndPropertyModal from "../HistoryAndPropertyModal/HistoryAndPropertyModal";
import {
  FileItemBlockingModal,
  IBlockingDataCurrentStateItem,
  IBlockingDataCurrentStateItemVersion,
  IBlockingDataRequestItem,
  IBlockingModal,
} from "./BlockingModalInterface";
import { hasLocalAccess } from "../../lib/configs";
import warningIcon from "../../img/warning";
import { BlockingRequestItem } from "../../lib/interfaces/Blocking";
import useBlockingMode from "../../lib/hooks/useBlockingMode";
import { getBlockingStatusOnlyIcon } from "../../lib/utils/getBlockingStatusIcon";
import LocalStorageService from "../../services/LocalStorageService";
import { ReactComponent as RefreshIcon } from "../../img/resetfresh-icon.svg";
import "./BlockingModal.less";
import { AccessToken } from "../../lib/types/AccessToken";

const columnsWidths = {
  ...versionsTableUtils.columnsWidths,
  period: "180px",
  actions: "",
  eye: "",
  buttons: "22px",
  requestStatusId: "120px",
};

const BlockingModal = ({
  objectTypeId,
  objectId,
  handleHideModal,
  visibleModal,
  dateObjectToForCalendar,
  agreementAccountId,
  serviceAgreementId,
  serviceAgreementAccountId,
  agreementNumber,
  accountName,
  serviceName,
  onSuccessRequest,
}: IBlockingModal) => {
  const dispatch = useAppDispatch();

  const nowDate = moment();

  const {
    blockingCurrentState,
    blockingCurrentStateLoading,
    blockingCurrentStateVersions,
    blockingCurrentStateVersionsLoading,
    blockingRequest,
    blockingRequestLoading,
    blockingTypes,
    blockingTypesLoading,
  } = useAppSelector((state) => state.blocking);

  const {
    blockingObjectType,
    blockingObjectTypeLoading,
    requestStatus,
    requestStatusLoading,
    blockingSource,
    blockingSourceLoading,
    blockingReason,
    blockingReasonLoading,
    unBlockingReason,
    unBlockingReasonLoading,
  } = useAppSelector((state: RootStateOrAny) => state.rtkDictionaries);

  const isBlockingMode = useBlockingMode();

  // Первоначальный запрос данных таблиц и справочника
  useEffect(() => {
    if (isBlockingMode) {
      if (objectId) {
        dispatch(
          fetchBlockingCurrentState({
            objectTypeId,
            objectId,
            onDate: objectTypeId === BlockingObjTypes.Продукт ? dateObjectToForCalendar : undefined,
          })
        );
        dispatch(
          fetchBlockingRequest({
            objectTypeId,
            objectId,
            includePastDates: false,
            includeFutureDates: true,
          })
        );
      }
      if (!checkIsLoaded(blockingObjectTypeLoading)) dispatch(fetchBlockingObjectType());
      if (!checkIsLoaded(blockingSourceLoading)) dispatch(fetchBlockingSource());
      if (!checkIsLoaded(requestStatusLoading)) dispatch(fetchRequestStatus());
      if (!checkIsLoaded(blockingTypesLoading)) dispatch(fetchBlockingTypes());
      if (!checkIsLoaded(blockingReasonLoading)) dispatch(fetchBlockingReason());
      if (!checkIsLoaded(unBlockingReasonLoading)) dispatch(fetchUnBlockingReason());
    }
  }, [objectId, objectTypeId, isBlockingMode]);

  // Очистка при закрытии
  useEffect(() => {
    return () => {
      dispatch(resetBlockingModalData());
    };
  }, []);

  // Все загруженные файлы в модалках создания и редактирования
  const [loadedFiles, setLoadedFiles] = useState<FileItemBlockingModal[]>([]);

  // Добавляем загруженные файлы из модалок создания и редактирования
  const onChageLoadFile = (newFiles) => {
    setLoadedFiles(newFiles);
  };

  const [visibleErrorModal, setVisibleErrorModal] = useState(false);

  // Состояние отображения модального окна истории и пропертей
  const [visibleModalHistoryAndProperty, setVisibleModalHistoryAndProperty] = useState(false);

  // ObjectId строки на которую кликнули открыть модальное окно истории и пропертей
  const [objectIdModalHistoryAndProperty, setObjectIdModalHistoryAndProperty] = useState(null);

  // Показ модального окна истории и пропертей
  const handleShowModal = (id) => {
    setVisibleModalHistoryAndProperty(true);
    setObjectIdModalHistoryAndProperty(id);
  };

  // Скрытие модального окна истории и пропертей
  const handleHideModalHistoryAndProperty = () => {
    setVisibleModalHistoryAndProperty(false);
    setObjectIdModalHistoryAndProperty(null);
  };

  // Инстансы форм модалок создания и редактирования
  const [formCreateForActionModal] = Form.useForm();
  const [formEditForActionModal] = Form.useForm();

  // Ключ редактируемой строки таблицы  "Заявки на блокировку"
  const [editingKey, setEditingKey] = useState<number | string | null>(null);

  // Редактируемая строка таблицы  "Заявки на блокировку"
  const [editingRow, setEditingRow] = useState<BlockingRequestItem | undefined>();

  // Проверка редактируется ли строка таблицы  "Заявки на блокировку"
  const isEditing = (rec) => rec.key === editingKey;

  // Состояние отображения модального окна редактирования "Заявки на блокировку"
  const [visibleReasonForActionModal, setVisibleReasonForActionModal] = useState({
    typeAction: "",
    visibleModal: false,
    requestId: null,
    requestStatusId: null,
    periodFrom: null,
  });

  // Состояние отображения модального окна создания "Заявки на блокировку"
  const [visibleReasonCreateForActionModal, setVisibleReasonCreateForActionModal] = useState({
    visibleModal: false,
  });

  // Выбранное значение в фильтре "Уровень блокировки"
  const [blockingObjectTypeSelected, setBlockingObjectTypeSelected] = useState(objectTypeId);

  // Выбранное значение в фильтре "Показать закрытые"
  const [viewClosedCheckbox, setViewClosedCheckbox] = useState<boolean>(false);

  // Данные таблицы "Текущее состояние"
  const [dataCurrentState, setDataCurrentState] = useState<IBlockingDataCurrentStateItem[]>([]);

  // Данные версий таблицы "Текущее состояние"
  const [dataCurrentStateVersions, setDataCurrentStateVersions] = useState<
    IBlockingDataCurrentStateItemVersion[]
  >([]);

  // Состояние отображения версий таблицы "Текущее состояние"
  const [
    expandedCurrentStateVersionsTableRows,
    setExpandedCurrentStateVersionsTableRows,
  ] = useState<number[]>([]);

  // Данные таблицы "Заявки на блокировку"
  const [dataRequest, setDataRequest] = useState<IBlockingDataRequestItem[]>([]);

  // Закрытие всей модалки
  const closeModal = () => {
    handleHideModal();
    setBlockingObjectTypeSelected(objectTypeId);
  };

  // Форматирование данных для отображения в таблице "Текущее состояние"
  useEffect(() => {
    setDataCurrentState(
      blockingCurrentState
        .filter((el) => el !== null)
        .map((item, index) => {
          return {
            ...item,
            key: uniqueId(),
            currentDate: moment().isBetween(item?.fromDate, item?.toDate),
            pastDate: moment(item?.toDate).isBefore(nowDate),
            futureDate: moment(item?.fromDate).isAfter(nowDate),
            toDateForRequestBody: item?.toDate,
            fromDateForRequestBody: item?.fromDate,
            lastVersion: index === 0 ? "last-version" : "",
          };
        })
    );
  }, [blockingCurrentState]);

  // Форматирование данных для отображения версий в таблице "Текущее состояние"
  useEffect(() => {
    setDataCurrentStateVersions(
      blockingCurrentStateVersions
        ?.map((item, i) => ({
          isCurrentVersion: moment().isBetween(item.fromDate, item.toDate),
          isPastVersion: moment(item.fromDate).isBefore(moment()),
          isFutureVersion: moment(item.fromDate).isAfter(moment()),
          key: uniqueId(),
          ...item,
        }))
        .sort((a, b) => (moment(a.fromDate).isBefore(moment(b.fromDate)) ? 1 : -1))
    );
  }, [blockingCurrentStateVersions]);

  const [filterAgreementsIdsForm] = Form.useForm();
  const [activeIds, setActiveIds] = useState<number[]>([]);
  const [selectedBlockingRequestStatuses, setSelectedBlockingRequestStatuses] = useState<number[]>(
    []
  );

  // Форматирование данных для отображения версий в таблице "Заявки на блокировку"
  useEffect(() => {
    setDataRequest(
      blockingRequest
        ?.filter((el) => el !== null)
        .filter(({ requestStatusId }) => {
          if (selectedBlockingRequestStatuses.length === 0) {
            return true;
          }
          return selectedBlockingRequestStatuses.includes(requestStatusId);
        })
        .filter(({ id }) => {
          if (activeIds.length === 0) {
            return true;
          }
          return activeIds.some((activeId) => String(id).startsWith(String(activeId)));
        })
        .map((item, index) => {
          return {
            ...item,
            key: uniqueId(),
            currentDate: moment().isBetween(item?.fromDate, item?.toDate),
            pastDate: moment(item?.toDate).isBefore(nowDate),
            futureDate: moment(item?.fromDate).isAfter(nowDate),
            toDateForRequestBody: item?.toDate,
            fromDateForRequestBody: item?.fromDate,
            lastVersion: index === 0 ? "last-version" : "",
            actions: 1,
          };
        })
    );
  }, [blockingRequest, activeIds, selectedBlockingRequestStatuses]);

  // Создание колонок для таблицы "Текущее состояние"
  const getColumnsCurrentState = () => {
    return [
      {
        title: "Результат",
        dataIndex: "blockingTypeId",
        key: "blockingTypeId",
        width: "200px",
        className: "columns-first whiteSpase-noWrap",
        render: (val) => {
          return (
            blockingTypes.find((el) => el.id === val)?.name || (
              <div style={{ visibility: "hidden" }}>-</div>
            )
          );
        },
      },
      {
        title: "Активные блокировки",
        dataIndex: "blockingList",
        key: "blockingList",
        width: "200px",
        className: "columns-first",
        render: (val) => {
          return (
            blockingTypes
              .filter((el) => val?.some((blockId) => blockId === el.id))
              .map((item) => item.name)
              .join(", ") || ""
          );
        },
      },
      {
        title: "Период действия",
        key: "period",
        dataIndex: "period",
        width: columnsWidths.period,
        className: "columns-first",
        render: (_, record) => {
          return (
            <div className="blocking-request-date-info-container">
              <div className="blocking-request-date-container">
                <p className="date-range-text">
                  {record.fromDate ? toDisplayFormat(record.fromDate) : ""}
                </p>
                -
                <p className="date-range-text">
                  {record.toDate ? toDisplayFormat(record.toDate) : ""}
                </p>
              </div>

              <div className="blocking-request-time-container">
                <p className="time-stamp-label">{toDisplayFormatOnlyTime(record.fromDate)}</p>
                <p className="time-label">{toDisplayFormatOnlyTime(record.toDate)}</p>
              </div>
            </div>
          );
        },
      },
      // {
      //   dataIndex: "",
      //   key: "button",
      // },
    ];
  };

  // Колонки таблицы "Текущее состояние"
  const [columnsCurrentState, setColumnsCurrentState] = useState(getColumnsCurrentState());

  // Иконка для сортировки по "ID" таблицы "Заявки на блокировку"
  const renderIdTitle = () => {
    const notValidRgx = /[^0-9,]/;
    let valueTrim = "";
    const onChangeId = ({ target: { value } }) => {
      valueTrim = value.replace(/^\s+/g, "");
      filterAgreementsIdsForm.setFieldsValue({ ids: valueTrim });
      if (!notValidRgx.test(value)) {
        const activeIdes = valueTrim?.split(/\D/).filter(Boolean).map(Number);
        setActiveIds(activeIdes);
      }
    };

    return (
      <Tooltip title="Введите ID заявок через ','">
        <Form form={filterAgreementsIdsForm}>
          <Form.Item
            name="ids"
            style={{ marginBottom: "-4px" }}
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              aria-autocomplete="none"
              placeholder="ID заявок"
              allowClear
              className="table-head-filter-search"
              suffix={searchIconForInput()}
              onChange={onChangeId}
            />
          </Form.Item>
        </Form>
      </Tooltip>
    );
  };

  renderIdTitle.toString = () => "ID";

  // Иконка для сортировки по "Статусу заявки" таблицы "Заявки на блокировку"
  const renderBlockingRequestStatusIdTitle = () => (
    <Select
      style={{ width: "130px", maxWidth: "130px" }}
      placeholder="Статус заявки"
      allowClear
      onChange={(val) => setSelectedBlockingRequestStatuses(val ? [val as number] : [])}
      // value={selectedBlockingRequestStatuses}
      showSearch
      filterOption={(inputValue, option) =>
        option?.children.toLowerCase().includes(inputValue.toLowerCase())
      }
    >
      {requestStatus.map(({ id, name }) => (
        <Select.Option key={id} value={id}>
          {name}
        </Select.Option>
      ))}
    </Select>
  );

  renderBlockingRequestStatusIdTitle.toString = () => "Статус заявки";

  // Иконка для открытия модалки создания "Заявки на блокировку"
  const renderAddIcon = () => {
    return (
      <div
        className="modal-create-property"
        style={{ display: "flex", justifyContent: "flex-end", paddingRight: 17 }}
      >
        <Tooltip title="Создать заявку">
          <Button
            icon={<img src={addProperty} alt="add property" width="21px" />}
            type="text"
            htmlType="button"
            className="button-icon"
            onClick={() => {
              setFromDateReason(moment());
              setToDateReason(null);
              setVisibleReasonCreateForActionModal({ visibleModal: true });
            }}
          />
        </Tooltip>
      </div>
    );
  };

  renderAddIcon.toString = () => "";

  const [selectedDate, setSelectedDate] = useState<{ fromDate?: string; toDate?: string }>({});

  // Создание колонок для таблицы "Заявки на блокировку"
  const getColumnsRequestsForBlocking = () => {
    return [
      {
        title: renderIdTitle,
        dataIndex: "id",
        key: "id",
        width: columnsWidths.id,
        className: "columns-first",
        hidden: true,
      },
      {
        title: "Блокировка",
        dataIndex: "blockingTypeId",
        key: "blockingTypeId",
        width: "",
        className: "columns-first whiteSpase-noWrap",
        hidden: false,
        render: (val) => {
          return blockingTypes.find((el) => el.id === val)?.name || "";
        },
      },
      {
        title: renderBlockingRequestStatusIdTitle,
        dataIndex: "requestStatusId",
        key: "requestStatusId",
        width: columnsWidths.requestStatusId,
        className: "columns-first whiteSpase-noWrap",
        hidden: false,
        render: (val) => {
          return requestStatus.find((el) => el.id === val)?.name || "";
        },
      },
      {
        title: "Источник",
        dataIndex: "sourceTypeId",
        key: "sourceTypeId",
        width: "",
        className: "columns-first whiteSpase-noWrap",
        hidden: false,
        render: (val) => {
          return blockingSource.find((el) => el.id === val)?.name || "";
        },
      },
      {
        title: "ID источника",
        dataIndex: "sourceId",
        key: "sourceId",
        width: "",
        className: "columns-first",
        hidden: true,
      },
      {
        title: "Период действия",
        dataIndex: "period",
        key: "period",
        width: columnsWidths.period,
        className: "columns-first",
        hidden: false,
        render: (val, record) => {
          const editable = isEditing(record);
          const fromDateValue = moment(record.periodFrom).add(1, "day");
          const toDateValue = moment(record.periodTo);

          const compareDates = () =>
            toDisplayFormat(record.periodFrom) === toDisplayFormat(record.periodTo);

          const onChangeFromDate = (e) => {
            setSelectedDate({
              ...selectedDate,
              fromDate: e,
            });
          };

          const onChangeToDate = (e) => {
            setSelectedDate({
              ...selectedDate,
              toDate: e,
            });
          };

          const disabledDates = (cur) => {
            if (compareDates()) {
              return cur;
            }

            return (
              cur.isBefore(fromDateValue) ||
              cur.isAfter(toDateValue) ||
              (selectedDate?.toDate && cur.isAfter(moment(selectedDate?.toDate).endOf("day")))
            );
          };

          const defaultValues = () => {
            if (compareDates()) {
              return undefined;
            }

            return fromDateValue;
          };

          if (editable) {
            return (
              <>
                {(record.requestStatusId === BlockingRequestStatusIds.Активна ||
                  record.requestStatusId ===
                    BlockingRequestStatusIds["В обработке(Деактивация)"]) && (
                  <>
                    <Row style={{ flexWrap: "nowrap" }}>
                      <div
                        className="blocking-request-date-info-container"
                        style={{ marginTop: "-2px" }}
                      >
                        <div className="blocking-request-date-container">
                          <p className="date-range-text" style={{ marginRight: 2 }}>
                            {record.periodFrom ? toDisplayFormat(record.periodFrom) : ""}
                          </p>
                        </div>
                        <div className="blocking-request-time-container">
                          <p className="time-stamp-label">
                            {toDisplayFormatOnlyTime(record.periodFrom)}
                          </p>
                        </div>
                      </div>
                      <div style={{ display: "flex", alignItems: "center", margin: "0 4px" }}>
                        {" - "}
                      </div>
                      <div>
                        <DatePicker
                          style={{ width: "165px" }}
                          format={displayFormatWithTime}
                          onChange={onChangeToDate}
                          defaultValue={toDateValue}
                          disabledDate={(current) =>
                            current < fromDateValue ||
                            current > moment(infinityDateHttpFormat) ||
                            current < moment().startOf("day")
                          }
                          allowClear={false}
                          showTime={{ format: "HH:mm:ss" }}
                          placeholder=""
                        />
                      </div>
                    </Row>
                  </>
                )}

                {(record.requestStatusId === BlockingRequestStatusIds.Создана ||
                  record.requestStatusId ===
                    BlockingRequestStatusIds["В обработке(Активация)"]) && (
                  <>
                    <DatePicker
                      format={displayFormat}
                      onChange={onChangeFromDate}
                      defaultValue={defaultValues()}
                      disabledDate={disabledDates}
                      allowClear={false}
                    />
                    {" - "}
                    <DatePicker
                      style={{ width: "165px" }}
                      format={displayFormatWithTime}
                      onChange={onChangeToDate}
                      defaultValue={toDateValue}
                      disabledDate={(current) =>
                        current < fromDateValue ||
                        current > moment(infinityDateHttpFormat) ||
                        current < moment().startOf("day") ||
                        (selectedDate?.fromDate
                          ? current.isBefore(moment(selectedDate?.fromDate).startOf("day"))
                          : false)
                      }
                      allowClear={false}
                      showTime={{ format: "HH:mm:ss" }}
                      placeholder=""
                    />
                  </>
                )}
              </>
            );
          }
          return (
            <div className="blocking-request-date-info-container">
              <div className="blocking-request-date-container">
                <p className="date-range-text">
                  {record.periodFrom ? toDisplayFormat(record.periodFrom) : ""}
                </p>
                -
                <p className="date-range-text">
                  {record.periodTo ? toDisplayFormat(record.periodTo) : ""}
                </p>
              </div>

              <div className="blocking-request-time-container">
                <p className="time-stamp-label">{toDisplayFormatOnlyTime(record.periodFrom)}</p>
                <p className="time-label">{toDisplayFormatOnlyTime(record.periodTo)}</p>
              </div>
            </div>
          );
        },
      },
      {
        title: renderAddIcon(),
        dataIndex: "",
        key: "x",
        width: columnsWidths.actions,
        className: "columns-first whiteSpase-noWrap",
        hidden: false,
        render: (_, record) => {
          const editable = isEditing(record);

          const handleEdit = () => {
            setEditingKey(() => record.key);
            const findRequestItem = blockingRequest?.find((el) => el.id === record.id);
            setEditingRow(findRequestItem);
            setSelectedDate({});
          };

          const handleCancel = () => {
            setEditingKey(null);
            setSelectedDate({});
          };

          const handleSubmit = () => {
            setVisibleReasonForActionModal((prev) => ({
              ...prev,
              visibleModal: true,
              typeAction: "submit",
              requestId: record.id,
            }));
          };

          const handleDelete = () => {
            if (record.requestStatusId === BlockingRequestStatusIds.Активна) {
              dispatch(
                checkDeactivationBlockingRequest({
                  requestId: record.id,
                  onSuccess: () => {
                    setVisibleReasonForActionModal((prev) => ({
                      ...prev,
                      visibleModal: true,
                      typeAction: "delete",
                      requestId: record.id,
                      periodFrom: record.periodFrom,
                    }));
                  },
                  onError: () => {
                    setVisibleErrorModal(true);
                  },
                })
              );
            } else {
              setVisibleReasonForActionModal((prev) => ({
                ...prev,
                visibleModal: true,
                typeAction: "delete",
                requestId: record.id,
                periodFrom: record.periodFrom,
              }));
            }
          };

          const isDisabledBtn = () => {
            let isDisabled = false;
            let isDisabledPermissions = false;

            if (record.blockingTypeId === 1) {
              isDisabledPermissions = !hasAccess(BLOCKING_DEBTS_DEACTIVATION);
            }

            isDisabled = ![
              BlockingRequestStatusIds.Создана,
              BlockingRequestStatusIds.Активна,
              BlockingRequestStatusIds["В обработке(Активация)"],
              BlockingRequestStatusIds["В обработке(Деактивация)"],
            ].some((el) => el === record.requestStatusId);

            return isDisabled || isDisabledPermissions;
          };

          return editable ? (
            <>
              <Tooltip title="Подтвердить">
                <Button
                  icon={<img src={tickIcon} alt="tick" width="16px" />}
                  className="button-icon"
                  type="text"
                  htmlType="button"
                  onClick={handleSubmit}
                />
              </Tooltip>
              <Tooltip title="Отменить">
                <Button
                  icon={<img src={deleteIcon} width="16px" alt="delete" />}
                  type="text"
                  className="button-icon"
                  htmlType="button"
                  onClick={handleCancel}
                />
              </Tooltip>
            </>
          ) : (
            <Row style={{ flexWrap: "nowrap" }}>
              <Tooltip title="Редактировать период">
                <Button
                  icon={
                    <img src={pencilWithCalendarIcon} alt="pencil with calendar" width="18px" />
                  }
                  type="text"
                  htmlType="button"
                  className="button-icon"
                  disabled={isDisabledBtn()}
                  onClick={handleEdit}
                />
              </Tooltip>
              <Tooltip title="Отменить заявку">
                <Button
                  icon={<img src={DeleteIcon} onClick={handleDelete} />}
                  type="text"
                  htmlType="button"
                  className="button-icon"
                  style={{ border: "none", backgroundColor: "transparent" }}
                  disabled={isDisabledBtn()}
                />
              </Tooltip>
            </Row>
          );
        },
      },
      {
        dataIndex: "",
        key: "button",
        width: columnsWidths.buttons,
        render: (record) => (
          <Button
            onClick={(e) => {
              e.stopPropagation();
              handleShowModal(record?.id);
            }}
            className="button-icon iconButton"
            style={{ outline: "none" }}
            icon={<DotsIcon />}
          />
        ),
      },
    ];
  };

  // Колонки таблицы "Заявки на блокировку"
  const [columnsRequestsForBlocking, setСolumnsRequestsForBlocking] = useState(
    getColumnsRequestsForBlocking()
  );

  // Обновление колонок таблицы "Текущее состояние"
  useEffect(() => {
    setColumnsCurrentState(getColumnsCurrentState());
  }, [blockingTypes]);

  // Обновление колонок таблицы "Заявки на блокировку"
  useEffect(() => {
    setСolumnsRequestsForBlocking(getColumnsRequestsForBlocking());
  }, [editingKey, blockingTypes, blockingSource, requestStatus, selectedDate, blockingRequest]);

  const innerRowClassName = (record) => {
    if (record.isCurrentVersion) {
      return `row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return "row_child row_past-version";
    }
    return "row_child";
  };

  // Таблица версий таблицы "Текущее состояние"
  const expandedRowCurrentStateRender = () => (
    <Table
      loading={!checkIsLoaded(blockingCurrentStateVersionsLoading)}
      showHeader={false}
      dataSource={dataCurrentStateVersions}
      columns={columnsCurrentState}
      className="table_no-hover blocking-versions-table__expand"
      rowClassName={innerRowClassName}
      pagination={false}
      expandable={{
        expandIconColumnIndex: 3,
        expandedRowRender: () => <></>,
        expandIcon: () => {
          return <Row style={{ minWidth: 34 }} />;
        },
      }}
    />
  );

  // Открытие версий таблицы "Текущее состояние"
  const onCurrentStateExpand = (record) => {
    dispatch(
      fetchBlockingCurrentStateVersions({
        objectTypeId: blockingObjectTypeSelected,
        objectId: record.objectId,
        onDate: objectTypeId === BlockingObjTypes.Продукт ? dateObjectToForCalendar : undefined,
      })
    );
    setExpandedCurrentStateVersionsTableRows([record.key]);
  };

  // Закрытие версий таблицы "Текущее состояние"
  const closeCurrentStateExpand = () => {
    setExpandedCurrentStateVersionsTableRows([]);
  };

  const onChangeBlockingObjectTypeSelected = (objectTypeIdSelected: BlockingObjTypes) => {
    setBlockingObjectTypeSelected(objectTypeIdSelected);
  };

  // Перезапрос всех таблиц при фильтре уровень блокировок
  useEffect(() => {
    if (isBlockingMode) {
      if (objectId) {
        switch (objectTypeId) {
          case BlockingObjTypes.Договор:
            if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт && agreementAccountId) {
              dispatch(
                fetchBlockingCurrentState({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: agreementAccountId,
                })
              );
              dispatch(
                fetchBlockingRequest({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: agreementAccountId,
                  includePastDates: viewClosedCheckbox,
                  includeFutureDates: !viewClosedCheckbox,
                })
              );
            }
            if (blockingObjectTypeSelected === BlockingObjTypes.Договор) {
              dispatch(
                fetchBlockingCurrentState({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId,
                })
              );
              dispatch(
                fetchBlockingRequest({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId,
                  includePastDates: viewClosedCheckbox,
                  includeFutureDates: !viewClosedCheckbox,
                })
              );
            }
            break;
          case BlockingObjTypes.Продукт:
            if (blockingObjectTypeSelected === BlockingObjTypes.Договор && serviceAgreementId) {
              dispatch(
                fetchBlockingCurrentState({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: serviceAgreementId,
                })
              );
              dispatch(
                fetchBlockingRequest({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: serviceAgreementId,
                  includePastDates: viewClosedCheckbox,
                  includeFutureDates: !viewClosedCheckbox,
                })
              );
            }
            if (
              blockingObjectTypeSelected === BlockingObjTypes.Аккаунт &&
              serviceAgreementAccountId
            ) {
              dispatch(
                fetchBlockingCurrentState({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: serviceAgreementAccountId,
                })
              );
              dispatch(
                fetchBlockingRequest({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId: serviceAgreementAccountId,
                  includePastDates: viewClosedCheckbox,
                  includeFutureDates: !viewClosedCheckbox,
                })
              );
            }
            if (blockingObjectTypeSelected === BlockingObjTypes.Продукт) {
              dispatch(
                fetchBlockingCurrentState({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId,
                })
              );
              dispatch(
                fetchBlockingRequest({
                  objectTypeId: blockingObjectTypeSelected,
                  objectId,
                  includePastDates: viewClosedCheckbox,
                  includeFutureDates: !viewClosedCheckbox,
                })
              );
            }
            break;
          default:
            break;
        }
      }
    }
  }, [blockingObjectTypeSelected]);

  const fetchTableCurrentState = () => {
    if (objectId) {
      switch (objectTypeId) {
        case BlockingObjTypes.Договор:
          if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт && agreementAccountId) {
            dispatch(
              fetchBlockingCurrentState({
                objectTypeId: blockingObjectTypeSelected,
                objectId: agreementAccountId,
              })
            );
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор) {
            dispatch(
              fetchBlockingCurrentState({
                objectTypeId: blockingObjectTypeSelected,
                objectId,
              })
            );
          }
          break;
        case BlockingObjTypes.Продукт:
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор && serviceAgreementId) {
            dispatch(
              fetchBlockingCurrentState({
                objectTypeId: blockingObjectTypeSelected,
                objectId: serviceAgreementId,
              })
            );
          }
          if (
            blockingObjectTypeSelected === BlockingObjTypes.Аккаунт &&
            serviceAgreementAccountId
          ) {
            dispatch(
              fetchBlockingCurrentState({
                objectTypeId: blockingObjectTypeSelected,
                objectId: serviceAgreementAccountId,
              })
            );
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Продукт) {
            dispatch(
              fetchBlockingCurrentState({
                objectTypeId: blockingObjectTypeSelected,
                objectId,
              })
            );
          }
          break;
        case BlockingObjTypes.Аккаунт:
          dispatch(
            fetchBlockingCurrentState({
              objectTypeId,
              objectId,
            })
          );
          break;
        default:
          break;
      }
    }
  };

  const fetchTableRequest = () => {
    if (objectId) {
      switch (objectTypeId) {
        case BlockingObjTypes.Договор:
          if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт && agreementAccountId) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId: agreementAccountId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          break;
        case BlockingObjTypes.Продукт:
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор && serviceAgreementId) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId: serviceAgreementId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          if (
            blockingObjectTypeSelected === BlockingObjTypes.Аккаунт &&
            serviceAgreementAccountId
          ) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId: serviceAgreementAccountId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Продукт) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          break;
        case BlockingObjTypes.Аккаунт:
          if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт) {
            dispatch(
              fetchBlockingRequest({
                objectTypeId: blockingObjectTypeSelected,
                objectId,
                includePastDates: viewClosedCheckbox,
                includeFutureDates: !viewClosedCheckbox,
              })
            );
          }
          break;
        default:
          break;
      }
    }
  };

  // Перезапрос таблицы "Заявки на блокировку" при изменении в фильтре показать закрытые
  useEffect(() => {
    if (isBlockingMode) {
      fetchTableRequest();
    }
  }, [viewClosedCheckbox]);

  // Форматирование данных для отображения в строках "ID" и "Наименование"
  const viewForFilter = (type: "id" | "name") => {
    if (objectId) {
      switch (objectTypeId) {
        case BlockingObjTypes.Договор:
          if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт && agreementAccountId) {
            return type === "id" ? agreementAccountId : accountName;
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор) {
            return type === "id" ? objectId : agreementNumber;
          }
          break;
        case BlockingObjTypes.Продукт:
          if (blockingObjectTypeSelected === BlockingObjTypes.Договор && serviceAgreementId) {
            return type === "id" ? serviceAgreementId : agreementNumber;
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Аккаунт) {
            return type === "id" ? serviceAgreementAccountId || "-" : accountName || "-";
          }
          if (blockingObjectTypeSelected === BlockingObjTypes.Продукт) {
            return type === "id" ? objectId : serviceName;
          }
          break;
        case BlockingObjTypes.Аккаунт:
          return type === "id" ? objectId : accountName;
        default:
          return "";
      }
    }
  };

  // Модальное окно редактирования "Заявки на блокировку"
  const reasonEditForActionModal = () => {
    const closeReasonEditForActionModal = () => {
      formEditForActionModal.resetFields();
      setVisibleReasonForActionModal((prev) => ({
        ...prev,
        visibleModal: false,
        typeAction: "",
        periodFrom: null,
      }));
      if (Array.isArray(loadedFiles) && loadedFiles.length > 0) {
        loadedFiles.forEach((file) => {
          axios.delete(deleteContentFileData(file.id));
        });
        setLoadedFiles([]);
      }
    };

    const onFinishEditForActionModal = (fields) => {
      if (visibleReasonForActionModal.typeAction === "delete") {
        if (objectId && visibleReasonForActionModal.requestId) {
          dispatch(
            deactivateBlockingRequest({
              requestId: visibleReasonForActionModal.requestId,
              body: {
                requestId: visibleReasonForActionModal?.requestId,
                fromDate: moment(visibleReasonForActionModal.periodFrom).isSameOrBefore(moment())
                  ? toHttpFormat(moment())
                  : visibleReasonForActionModal.periodFrom,
                reasonUnblockingId: fields.reasonTypeId,
                reasonContentIds: loadedFiles.map((file) => file?.id),
                dsc: fields.dsc,
              },
              onSuccess: () => {
                formEditForActionModal.resetFields();
                setVisibleReasonForActionModal((prev) => ({
                  typeAction: "",
                  visibleModal: false,
                  requestId: null,
                  requestStatusId: null,
                  periodFrom: null,
                }));

                setTimeout(() => {
                  fetchTableCurrentState();
                  fetchTableRequest();
                  onSuccessRequest && onSuccessRequest();
                }, 500);

                setLoadedFiles([]);
              },
              onError: () => {
                formEditForActionModal.resetFields();
                setVisibleReasonForActionModal((prev) => ({
                  typeAction: "",
                  visibleModal: false,
                  requestId: null,
                  requestStatusId: null,
                  periodFrom: null,
                }));
                if (Array.isArray(loadedFiles) && loadedFiles.length > 0) {
                  loadedFiles.forEach((file) => {
                    axios.delete(deleteContentFileData(file.id));
                  });
                  setLoadedFiles([]);
                }
              },
              onErrorForNotErrorCode: () => {
                setVisibleErrorModal(true);
              },
            })
          );
        }
      }

      if (visibleReasonForActionModal.typeAction === "submit") {
        if (objectId && visibleReasonForActionModal?.requestId) {
          const body = {
            ...editingRow,
            periodTo: selectedDate?.toDate
              ? toHttpFormat(selectedDate.toDate)
              : toHttpFormat(editingRow?.periodTo),
            periodFrom: selectedDate?.fromDate ? toHttpFormat(selectedDate.fromDate) : undefined,
          };

          if (!selectedDate?.fromDate) {
            delete body.periodFrom;
          }

          dispatch(
            editBlockingRequest({
              requestId: visibleReasonForActionModal.requestId,
              fromDate: selectedDate?.fromDate
                ? toHttpFormat(selectedDate.fromDate)
                : moment(editingRow?.periodFrom).utc().format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
              body,
              bodyReason: {
                reasonTypeId: fields.reasonTypeId,
                contentIds: loadedFiles.map((file) => file?.id),
                dsc: fields.dsc,
                reasonDicTypeId: "blocking_reason",
                createDate: toHttpFormat(moment()),
              },
              onSuccess: () => {
                formEditForActionModal.resetFields();
                setVisibleReasonForActionModal((prev) => ({
                  typeAction: "",
                  visibleModal: false,
                  requestId: null,
                  requestStatusId: null,
                  periodFrom: null,
                }));
                setTimeout(() => {
                  fetchTableCurrentState();
                  fetchTableRequest();
                  onSuccessRequest && onSuccessRequest();
                }, 500);
                setLoadedFiles([]);
              },
              onError: () => {
                formEditForActionModal.resetFields();
                setVisibleReasonForActionModal((prev) => ({
                  typeAction: "",
                  visibleModal: false,
                  requestId: null,
                  requestStatusId: null,
                  periodFrom: null,
                }));
                if (Array.isArray(loadedFiles) && loadedFiles.length > 0) {
                  loadedFiles.forEach((file) => {
                    axios.delete(deleteContentFileData(file.id));
                  });
                  setLoadedFiles([]);
                }
              },
            })
          );
        }
      }
    };

    const selectedReasonSelectOptons =
      visibleReasonForActionModal.typeAction === "delete" ? unBlockingReason : blockingReason;

    return (
      <ModalWindow
        visible={visibleReasonForActionModal.visibleModal}
        title="Основание действия"
        handleSubmit={() => formEditForActionModal.submit()}
        handleCancel={closeReasonEditForActionModal}
        handleReset={closeReasonEditForActionModal}
        cancelTextButton="Отмена"
        submitTextButton="Сохранить"
        width={641}
        disabled={false}
      >
        <Row style={{ width: "100%" }}>
          <Form
            form={formEditForActionModal}
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            labelAlign="left"
            style={{ width: "100%", padding: "19.5px 95px 24.5px" }}
            onFinish={onFinishEditForActionModal}
          >
            <Form.Item
              name="reasonTypeId"
              label="Тип основания"
              rules={[
                {
                  required: true,
                  message: "Заполните данные для основания действия",
                },
              ]}
            >
              <Select>
                {selectedReasonSelectOptons.map((item) => {
                  return (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item name="dsc" label="Комментарий:">
              <TextArea />
            </Form.Item>
            <Form.Item className="whiteSpase-normal" label="Прикрепленные документы:">
              <div>
                <FileList files={loadedFiles} onChageLoadFile={onChageLoadFile} />
              </div>
            </Form.Item>
          </Form>
        </Row>
      </ModalWindow>
    );
  };

  // Сострояние создания "Заявки на блокировку"
  const [isLoadingCreateBlocked, setIsLoadingCreateBlocked] = useState(false);

  const [fromDateReason, setFromDateReason] = useState(moment());
  const [toDateReason, setToDateReason] = useState(null);

  const onChangeFromDateReason = (value) => {
    setFromDateReason(value);
  };

  const onChangeToDateReason = (value) => {
    setToDateReason(value);
  };

  // Модальное окно создания "Заявки на блокировку"
  const reasonCreateForActionModal = () => {
    const closeReasonCreateForActionModal = () => {
      formCreateForActionModal.resetFields();
      setVisibleReasonCreateForActionModal((prev) => ({ ...prev, visibleModal: false }));
      if (Array.isArray(loadedFiles) && loadedFiles.length > 0) {
        loadedFiles.forEach((file) => {
          axios.delete(deleteContentFileData(file.id));
        });
        setLoadedFiles([]);
      }
      setIsLoadingCreateBlocked(false);
    };

    const onFinishCreateForActionModal = (fields) => {
      if (objectId) {
        setIsLoadingCreateBlocked(true);

        const token = LocalStorageService.getAccessToken();
        const userId = token ? jwtDecode<AccessToken>(token).user_id : undefined;

        dispatch(
          createBlockingRequest({
            objectTypeId: blockingObjectTypeSelected,
            objectId: Number(viewForFilter("id")),
            body: {
              periodFrom: fields.periodFrom
                ? toHttpFormatStartOfDay(fields.periodFrom)
                : toHttpFormat(moment()),
              periodTo: fields.periodTo ? toHttpFormatEndOfDay(fields.periodTo) : undefined,
              blockingType: blockingTypes.find((el) => el.id === fields.blockingTypeId)?.alias,
              reasonBlockingId: fields.reasonBlockingId,
              reasonContentIds: loadedFiles.map((file) => file?.id),
              sourceTypeId: 1, // TODO
              // передавать в параметр "sourceId" userId из токена авторизации, если sourceTypeId === 1
              sourceId: userId,
              dsc: fields.dsc,
            },
            onSuccess: () => {
              formCreateForActionModal.resetFields();
              setVisibleReasonCreateForActionModal((prev) => ({ ...prev, visibleModal: false }));
              setTimeout(() => {
                fetchTableCurrentState();
                fetchTableRequest();
                onSuccessRequest && onSuccessRequest();
              }, 500);
              setLoadedFiles([]);
              setIsLoadingCreateBlocked(false);
            },
            onError: () => {
              formCreateForActionModal.resetFields();
              setVisibleReasonCreateForActionModal((prev) => ({ ...prev, visibleModal: false }));
              if (Array.isArray(loadedFiles) && loadedFiles.length > 0) {
                loadedFiles.forEach((file) => {
                  axios.delete(deleteContentFileData(file.id));
                });
              }
              setIsLoadingCreateBlocked(false);
            },
            onClearLoading: () => {
              setIsLoadingCreateBlocked(false);
            },
          })
        );
      }
    };

    const disabledFromDate = (current) => current.isAfter(toDateReason, "day");

    const disabledToDate = (current) => current.isBefore(fromDateReason, "day");

    const objectTypeId = blockingObjectType.find((el) => el.id === blockingObjectTypeSelected);

    return (
      <ModalWindow
        visible={visibleReasonCreateForActionModal.visibleModal}
        title="Создать заявку на блокировку"
        handleSubmit={() => formCreateForActionModal.submit()}
        handleCancel={closeReasonCreateForActionModal}
        handleReset={closeReasonCreateForActionModal}
        cancelTextButton="Отмена"
        submitTextButton="Сохранить"
        width={641}
        disabled={isLoadingCreateBlocked}
        submitButtonLoading={isLoadingCreateBlocked}
      >
        <Row style={{ width: "100%" }}>
          <Form
            form={formCreateForActionModal}
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            labelAlign="left"
            style={{ width: "100%", padding: "19.5px 65px 0px" }}
            onFinish={onFinishCreateForActionModal}
            initialValues={{ periodFrom: moment() }}
          >
            <Form.Item name="objectTypeId" label="Уровень блокировки" initialValue={objectTypeId}>
              {objectTypeId?.name}
            </Form.Item>
            <Form.Item label="Наименование">
              <Tooltip title={viewForFilter("id")}>{viewForFilter("name")}</Tooltip>
            </Form.Item>
            <Form.Item
              name="blockingTypeId"
              label="Тип блокировки"
              rules={[
                {
                  required: true,
                  message: "",
                },
              ]}
            >
              <Select>
                {blockingTypes
                  .filter((item) => {
                    if (hasAccess(BLOCKING_DEBTS_DEACTIVATION)) {
                      return true;
                    }
                    // Если нет привелегии не показывать финансовую блокировку
                    return item.id !== 1;
                  })
                  .map((item) => (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  ))}
              </Select>
            </Form.Item>
            <Form.Item name="periodFrom" label="Период С">
              <DatePicker
                format={displayFormat}
                onChange={onChangeFromDateReason}
                disabledDate={disabledFromDate}
              />
            </Form.Item>
            <Form.Item name="periodTo" label="Период По">
              <DatePicker
                format={displayFormat}
                onChange={onChangeToDateReason}
                disabledDate={disabledToDate}
              />
            </Form.Item>

            <div style={{ margin: "0 -89px", height: 53 }}>
              <Divider style={{ margin: "0 0 15px 0" }} />
              <div style={{ paddingLeft: 16 }} className="ant-modal-title">
                Основание действия
              </div>
            </div>

            <Form.Item
              name="reasonBlockingId"
              label="Тип основания"
              rules={[
                {
                  required: true,
                  message: "Заполните данные для основания действия",
                },
              ]}
            >
              <Select>
                {blockingReason.map((item) => {
                  return (
                    <Select.Option key={item.id} value={item.id}>
                      {item.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
            <Form.Item name="dsc" label="Комментарий:">
              <TextArea />
            </Form.Item>
            <Form.Item className="whiteSpase-normal" label="Прикрепленные документы:">
              <div>
                <FileList files={loadedFiles} onChageLoadFile={onChageLoadFile} />
              </div>
            </Form.Item>
          </Form>
        </Row>
      </ModalWindow>
    );
  };

  // Модальное окно ошибки "Заявки на блокировку"
  const errorModal = () => {
    const closeErrorModal = () => {
      setVisibleErrorModal(false);
    };

    return (
      <Modal
        onCancel={closeErrorModal}
        centered
        visible={visibleErrorModal}
        width={500}
        footer={[
          <Button key="2" onClick={closeErrorModal} style={{ width: "107px" }} type="primary">
            Ок
          </Button>,
        ]}
      >
        <div
          style={{
            fontSize: "14px",
            display: "flex",
            justifyContent: "center",
            marginLeft: "15px",
          }}
        >
          <div style={{ marginRight: 20 }}>{warningIcon}</div>
          <span style={{ paddingLeft: "10px", paddingTop: "2px" }}>Отмена заявки недоступна</span>
        </div>
      </Modal>
    );
  };

  const filterBlockingObjectType = (id) => {
    if (objectTypeId === BlockingObjTypes.Договор) {
      return id === BlockingObjTypes.Договор || id === BlockingObjTypes.Аккаунт;
    }
    if (objectTypeId === BlockingObjTypes.Продукт) {
      return (
        id === BlockingObjTypes.Договор ||
        id === BlockingObjTypes.Аккаунт ||
        id === BlockingObjTypes.Продукт
      );
    }
    if (objectTypeId === BlockingObjTypes.Аккаунт) {
      return id === BlockingObjTypes.Аккаунт;
    }
  };

  const onClose = () => {
    setViewClosedCheckbox(false);
    closeModal();
  };

  return (
    <ModalWindow
      className="blocking__modal"
      width={1551}
      visible={visibleModal}
      handleCancel={onClose}
      handleSubmit={onClose}
      disabled={false}
      footerHide
    >
      <div className="blocking_container">
        <div className="inner-row blocking_container_header">
          <h1>Блокировки</h1>
        </div>
        <div className="blocking_container_body">
          <div className="blocking_container_body_filter">
            <Row gutter={80}>
              <Col style={{ display: "flex", flexWrap: "nowrap" }}>
                <div className="centered-text">Уровень блокировок</div>
                <Select
                  value={blockingObjectTypeSelected}
                  onChange={(val) => onChangeBlockingObjectTypeSelected(val)}
                  style={{ marginLeft: 13, maxWidth: 144, width: 144 }}
                  disabled={objectTypeId === BlockingObjTypes.Аккаунт}
                >
                  {blockingObjectType
                    .filter((item) => filterBlockingObjectType(item.id))
                    .map((item) => (
                      <Select.Option key={item.id} value={item.id}>
                        {item.name}
                      </Select.Option>
                    ))}
                </Select>
              </Col>
              <Col>
                <div className="centered-text">ID: {viewForFilter("id")}</div>
              </Col>
              <Col>
                <div className="centered-text">Наименование: {viewForFilter("name")}</div>
              </Col>
            </Row>
          </div>
          <div className="blocking_container_body_tables">
            <Row style={{ minHeight: 292 }}>
              <Col span={11} style={{ borderRight: "2px solid #D9D9D9" }}>
                <Row>
                  <div className="blocking_container_body_tables_header-table">
                    Текущее состояние
                  </div>
                  <div style={{ maxHeight: 22, marginLeft: "-15px", marginTop: "-3px" }}>
                    {getBlockingStatusOnlyIcon(dataCurrentState[0]?.blockingTypeId)}
                  </div>
                  <div style={{ marginTop: "-6px" }}>
                    <Button
                      onClick={() => {
                        fetchTableCurrentState();
                        fetchTableRequest();
                      }}
                      icon={<RefreshIcon />}
                      className="button-icon button-icon-refresh"
                      disabled={checkIsLoading(blockingCurrentStateLoading, blockingRequestLoading)}
                    />
                  </div>
                </Row>
                <Table
                  loading={!checkIsLoaded(blockingCurrentStateLoading)}
                  columns={columnsCurrentState}
                  pagination={false}
                  dataSource={dataCurrentState}
                  className="table_no-hover"
                  expandedRowKeys={expandedCurrentStateVersionsTableRows}
                  expandable={{
                    expandIconColumnIndex: 3,
                    expandedRowRender: expandedRowCurrentStateRender,
                    expandIcon: ({ expanded, record }) => {
                      if (!expanded) {
                        return (
                          <Row justify="end" onClick={(e) => e.stopPropagation()}>
                            <Button
                              icon={expandedTableEyeIcon("#86ACD2")}
                              style={{ boxShadow: "none", border: 0, paddingTop: 9 }}
                              onClick={() => onCurrentStateExpand(record)}
                              type="text"
                              htmlType="button"
                              className="button-icon"
                            />
                          </Row>
                        );
                      }
                      if (expanded) {
                        return (
                          <Row justify="end" onClick={(e) => e.stopPropagation()}>
                            <Button
                              icon={expandedTableEyeIcon("#0E5298")}
                              style={{ boxShadow: "none", border: 0, paddingTop: 9 }}
                              onClick={closeCurrentStateExpand}
                              type="text"
                              htmlType="button"
                              className="button-icon"
                            />
                          </Row>
                        );
                      }
                      return false;
                    },
                  }}
                  scroll={{ x: "100%" }}
                />
              </Col>
              <Col span={13}>
                <div
                  className="blocking_container_body_tables_header-table"
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  Заявки на блокировку
                  <div style={{ display: "flex" }}>
                    <Checkbox
                      style={{ marginTop: 3 }}
                      checked={viewClosedCheckbox}
                      onChange={(e) => setViewClosedCheckbox(e.target.checked)}
                    >
                      Показать закрытые
                    </Checkbox>
                    {!isEmpty(columnsRequestsForBlocking) && (
                      <ToggleColumns
                        tableName="blocking-request"
                        columns={columnsRequestsForBlocking}
                        setColumns={setСolumnsRequestsForBlocking}
                      />
                    )}
                  </div>
                </div>
                <Table
                  loading={!checkIsLoaded(blockingRequestLoading)}
                  columns={columnsRequestsForBlocking.filter((item) =>
                    item.key == "x" ? true : !item.hidden
                  )}
                  pagination={false}
                  dataSource={dataRequest}
                  className="table_no-hover"
                  scroll={{ x: 300 }}
                  rowClassName={(record) => {
                    if (
                      record.requestStatusId === BlockingRequestStatusIds.Отменена ||
                      record.requestStatusId === BlockingRequestStatusIds.Неактивна
                    )
                      return "greyColor";
                    return "";
                  }}
                />
              </Col>
            </Row>
          </div>
        </div>
      </div>
      {reasonEditForActionModal()}
      {reasonCreateForActionModal()}
      {errorModal()}
      <HistoryAndPropertyModal
        objectTypeSysname="blocking_request"
        objectId={objectIdModalHistoryAndProperty}
        handleHideModal={handleHideModalHistoryAndProperty}
        visibleModal={visibleModalHistoryAndProperty}
        // dateObjectFromForCalendar={objectIdModalHistoryAndPropertyFromDate}
        // dateObjectToForCalendar={objectIdModalHistoryAndPropertyToDate}
        // hasAccessToEdit={hasAccess(MENU_ADMIN_BLOCKING_TYPE_WRITE])}
        hasAccessToEdit
      />
    </ModalWindow>
  );
};

export default BlockingModal;
